/* eslint-disable @typescript-eslint/no-empty-function */
import { Address, UserInfo } from 'types';
import {
  CartItemInterface,
  ProductFieldsFragment,
  ProductInterface,
} from 'graphql/generated/magentoApi';
import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  ReactElement,
  useCallback,
} from 'react';
import {
  useGetCart,
  useCartId,
  useZeroCartId,
  useGetZeroCart,
} from 'hooks/magentoHooks';
import { useGetUserInfo } from 'hooks/userHooks';
import skuUrls from 'crwdshop.json';

export interface CartItem {
  product: ProductInterface;
  quantity: number;
  uid: string;
}

export interface Order {
  items: CartItemInterface[];
  orderNumber: string;
}

export interface CartContextType {
  billingAddress?: Address;
  cartId: string;
  zeroCartId: string;
  email?: string;
  hideNav: boolean;
  items: CartItemInterface[];
  lastOrder?: Order;
  loading: boolean;
  loggedInUserId?: string;
  orderNumber?: string;
  quantity: string;
  shipping: string;
  shippingAddress?: Address;
  subTotal: string;
  taxes: string;
  total: string;
  userInfo?: UserInfo;
  removeItemByUid: (uid: string) => void;
  reset: () => void;
  setBillingAddress: (billingAddress?: Address) => void;
  setHideNav: (hideNav: boolean) => void;
  setLoggedInUserId: (userId?: string) => void;
  setOrderNumber: (orderNumber?: string) => void;
  setShippingAddress: (shippingAddress?: Address) => void;
  setUserInfo: (userInfo?: UserInfo) => void;
  updateItems: (items: CartItemInterface[]) => void;
  getProductWebsiteUrl: (
    product: ProductFieldsFragment | ProductInterface,
  ) => string | undefined;
}

export interface CartProviderProps {
  children: ReactNode | ReactNode[];
}

export const CartContext = createContext<CartContextType>({
  cartId: '',
  zeroCartId: '',
  hideNav: false,
  items: [],
  loading: false,
  quantity: '0',
  shipping: 'free',
  subTotal: '0.00',
  taxes: '0.00',
  total: '0.00',
  removeItemByUid: (_uid: string) => {},
  reset: () => {},
  setBillingAddress: (_billingAddress?: Address) => {},
  setHideNav: (_hideNav: boolean) => {},
  setLoggedInUserId: (_userId?: string) => {},
  setOrderNumber: (_orderNumber?: string) => {},
  setShippingAddress: (_shippingAddress?: Address) => {},
  setUserInfo: (_userInfo?: UserInfo) => {},
  updateItems: (_items: CartItemInterface[]) => {},
  getProductWebsiteUrl: (_product: ProductFieldsFragment | ProductInterface) =>
    undefined,
});

export const cartQuantityStorageKey = 'cart-quantity';

export const useCartContext = (): CartContextType => useContext(CartContext);

export function CartProvider(props: CartProviderProps): ReactElement {
  const { children } = props;

  const { cartId, reset: resetCartId } = useCartId();
  const { zeroCartId, reset: resetZeroCartId } = useZeroCartId();
  const { cart, cartFetching, fetchCart } = useGetCart();
  const { zeroCart, zeroCartFetching, fetchZeroCart } = useGetZeroCart();
  const { userInfo, setUserInfo, loggedInUserId, setLoggedInUserId } =
    useGetUserInfo();

  const [billingAddress, setBillingAddress] = useState<Address | undefined>();
  const [hideNav, setHideNav] = useState(false);
  const [items, setItems] = useState<CartItemInterface[]>([]);
  const [lastOrder, setLastOrder] = useState<Order | undefined>();
  const [orderNumber, setOrderNumber] = useState<string>();
  const [quantity, setQuantityLocal] = useState(
    localStorage.getItem(cartQuantityStorageKey) || '0',
  );
  const [shipping, setShipping] = useState('free');
  const [shippingAddress, setShippingAddress] = useState<Address | undefined>();
  const [subTotal, setSubTotal] = useState('0.00');
  const [taxes, setTaxes] = useState('0.00');
  const [total, setTotal] = useState('0.00');
  const [userIsTracked, setUserIsTracked] = useState(false);
  const [skuIndexMap, setSkuIndexMap] = useState<Record<string, number>>({});

  const setQuantity = (newQuantity: string) => {
    localStorage.setItem(cartQuantityStorageKey, newQuantity);
    setQuantityLocal(newQuantity);
  };

  useEffect(() => {
    if (
      !items.length &&
      (cart?.items.length || zeroCart?.items.length) &&
      cart &&
      zeroCart &&
      !(cartFetching || zeroCartFetching)
    ) {
      setItems([...cart.items, ...zeroCart.items]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, zeroCart]);

  useEffect(() => {
    const newQuantity =
      items?.reduce((previous, item) => previous + item.quantity, 0) || 0;
    setQuantity(`${newQuantity}`);

    const newSubTotal = items.reduce(
      (previousTotal, item) =>
        previousTotal +
        item.product.price_range.maximum_price.final_price.value *
          item.quantity,
      0,
    );
    setSubTotal(newSubTotal.toFixed(2));

    const newTaxes =
      Number(
        cart?.prices?.applied_taxes?.reduce(
          (prev, tax) => prev + tax?.amount.value || 0,
          0,
        ),
      ) +
        Number(
          zeroCart?.prices?.applied_taxes?.reduce(
            (prev, tax) => prev + tax?.amount.value || 0,
            0,
          ),
        ) || 0;

    setTaxes(String(newTaxes.toFixed(2)));

    const newShipping = 0;

    const newTotal = newSubTotal + newTaxes + newShipping;
    setTotal(newTotal.toFixed(2));
  }, [cart, zeroCart, items, cartFetching, zeroCartFetching]);

  useEffect(() => {
    fetchCart();
    fetchZeroCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress]);

  // Lead tracking
  useEffect(() => {
    const userId = loggedInUserId || userInfo?.id || userInfo?.email;
    if (userId && !userIsTracked) {
      (window as any).LeadTracker?.recordLead(userId, {
        ...userInfo,
      });
      setUserIsTracked(true);
    }
  }, [userIsTracked, userInfo, loggedInUserId]);

  useEffect(() => {
    const userId = loggedInUserId || userInfo?.id || userInfo?.email;
    if (userId && orderNumber) {
      (window as any).LeadTracker?.recordPurchase(userId, {
        ...userInfo,
        orderNumber,
        items,
      });
    }
    if (orderNumber) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, userInfo, loggedInUserId]);

  const updateItems = (cartItems: CartItemInterface[]) => {
    setItems([...cartItems]);
  };

  const removeItemByUid = (uid: string) => {
    setItems(items.filter(item => item.uid !== uid));
  };

  const getProductWebsiteUrl = useCallback(
    (product: ProductFieldsFragment | ProductInterface): string | undefined => {
      const { sku, website_url } = product;
      if (!sku) {
        return undefined;
      }
      const urls: string[] =
        skuUrls[sku]?.map(info => {
          if (typeof info === 'string') {
            return info;
          }
          return info.domain;
        }) || [];

      let index = skuIndexMap[sku];
      if (index === undefined) {
        index = Math.floor(Math.random() * urls.length);
      } else {
        index += 1;
        if (index >= urls.length) {
          index = 0;
        }
      }

      setSkuIndexMap({
        ...skuIndexMap,
        [sku]: index,
      });

      let url = urls[index];
      if (url && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      return url || website_url || undefined;
    },
    [skuIndexMap],
  );

  const reset = () => {
    if (orderNumber) {
      setLastOrder({
        orderNumber,
        items,
      });
    }
    resetCartId();
    resetZeroCartId();
    setItems([]);
    setOrderNumber(undefined);
    setQuantity('0');
    setShipping('free');
    setSubTotal('0.00');
    setTotal('0.00');
  };

  const cartContextValue = {
    billingAddress,
    cartId,
    zeroCartId,
    hideNav,
    items,
    lastOrder,
    loading: cartFetching || zeroCartFetching,
    loggedInUserId,
    orderNumber,
    quantity,
    shipping,
    shippingAddress,
    subTotal,
    taxes,
    total,
    userInfo,
    removeItemByUid,
    reset,
    setBillingAddress,
    setHideNav,
    setLoggedInUserId,
    setOrderNumber,
    setShippingAddress,
    setUserInfo,
    updateItems,
    getProductWebsiteUrl,
  };

  return (
    <CartContext.Provider value={cartContextValue}>
      {children}
    </CartContext.Provider>
  );
}
